import React, { useState, useEffect } from "react";
import { Alert, ListGroup } from "react-bootstrap";
import { FiBluetooth, FiPrinter, FiCheck, FiX } from "react-icons/fi";
import api from "../services/api";
import bluetoothPrinter from "../utils/bluetooth";

const Printer = () => {
  const [printer, setPrinter] = useState({ connected: false, deviceName: "" });
  const [printerLoading, setPrinterLoading] = useState(false);
  const [printerMsg, setPrinterMsg] = useState({ type: "", text: "" });
  const [reconnecting, setReconnecting] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => setSettings(res.data || {}))
      .catch(() => {});

    // Cek status sekarang
    setPrinter({
      connected: bluetoothPrinter.isConnected(),
      deviceName: bluetoothPrinter.isConnected()
        ? bluetoothPrinter.device?.name || ""
        : "",
    });

    // Auto-reconnect ke printer yg pernah dipasangkan
    (async () => {
      try {
        const res = await bluetoothPrinter.autoReconnect();
        if (res.success) {
          setPrinter({ connected: true, deviceName: res.deviceName });
        }
      } catch (e) {
        /* silent */
      }
    })();

    // Deteksi printer terputus
    const onDisc = async (device) => {
      setPrinter({ connected: false, deviceName: device?.name || "" });
      setPrinterMsg({
        type: "warning",
        text: "Printer terputus. Mencoba menyambung kembali...",
      });
      setReconnecting(true);
      try {
        const res = await bluetoothPrinter.connect({ retries: 2 });
        if (res.success) {
          setPrinter({ connected: true, deviceName: res.deviceName });
          setPrinterMsg({
            type: "success",
            text: `Kembali terhubung: ${res.deviceName}`,
          });
        } else {
          setPrinterMsg({
            type: "danger",
            text: `Gagal reconnect: ${res.error}`,
          });
        }
      } catch (e) {
        setPrinterMsg({
          type: "danger",
          text: "Gagal menyambung kembali secara otomatis",
        });
      } finally {
        setReconnecting(false);
      }
    };
    bluetoothPrinter.onDisconnected(onDisc);
    return () => bluetoothPrinter.removeDisconnected(onDisc);
  }, []);

  const handleConnect = async () => {
    if (!bluetoothPrinter.isSupported()) {
      setPrinterMsg({
        type: "danger",
        text: "Web Bluetooth tidak didukung. Gunakan Chrome atau Edge.",
      });
      return;
    }
    setPrinterLoading(true);
    setPrinterMsg({ type: "", text: "" });
    try {
      const res = await bluetoothPrinter.connect();
      if (res.success) {
        setPrinter({ connected: true, deviceName: res.deviceName });
        setPrinterMsg({
          type: "success",
          text: `Terhubung ke: ${res.deviceName}`,
        });
        await api.put("/settings", { printer_name: res.deviceName });
      } else {
        setPrinterMsg({
          type: "danger",
          text: `Gagal terhubung: ${res.error}`,
        });
      }
    } catch (e) {
      setPrinterMsg({
        type: "danger",
        text: "Terjadi kesalahan saat menghubungkan printer",
      });
    } finally {
      setPrinterLoading(false);
    }
  };

  const handleDisconnect = async () => {
    await bluetoothPrinter.disconnect();
    setPrinter({ connected: false, deviceName: "" });
    setPrinterMsg({ type: "info", text: "Printer berhasil diputus" });
  };

  const handleTestPrint = async () => {
    if (!printer.connected) {
      setPrinterMsg({ type: "warning", text: "Printer belum terhubung" });
      return;
    }
    setPrinterLoading(true);
    setPrinterMsg({ type: "", text: "" });
    try {
      const testData = {
        invoice_number: "TEST-001",
        created_at: new Date(),
        cashier_name: "Kasir",
        customer_name: "Test Customer",
        plate_number: "B1234XYZ",
        vehicle_type: "Mobil",
        details: [
          {
            item_type: "service",
            item_name: "Cuci Mobil Reguler",
            unit_price: 25000,
            quantity: 1,
            subtotal: 25000,
          },
          {
            item_type: "product",
            item_name: "Pengharum Mobil",
            unit_price: 10000,
            quantity: 1,
            subtotal: 10000,
          },
        ],
        total_amount: 35000,
        discount_amount: 0,
        paid_amount: 50000,
        payment_method: "cash",
      };
      const res = await bluetoothPrinter.printReceipt(testData, settings);
      if (res.success) {
        setPrinterMsg({
          type: "success",
          text: "Test print berhasil! Periksa printer Anda.",
        });
      } else {
        setPrinterMsg({
          type: "danger",
          text: `Test print gagal: ${res.error}`,
        });
      }
    } catch (e) {
      setPrinterMsg({
        type: "danger",
        text: "Terjadi kesalahan saat mencetak",
      });
    } finally {
      setPrinterLoading(false);
    }
  };

  return (
    <div>
      <h4 className="mb-4">Printer Bluetooth</h4>
      <div className="card p-4" style={{ maxWidth: "500px" }}>
        {printerMsg.text && (
          <Alert
            variant={printerMsg.type}
            dismissible
            onClose={() => setPrinterMsg({ type: "", text: "" })}
            className="mb-3"
          >
            {printerMsg.text}
          </Alert>
        )}

        {reconnecting && (
          <Alert variant="warning" className="mb-3">
            Mencoba menyambung kembali ke printer...
          </Alert>
        )}

        <div className="d-flex align-items-center p-3 bg-light rounded mb-3">
          {printer.connected ? (
            <>
              <FiCheck className="text-success fs-3 me-3" />
              <div>
                <div className="fw-bold text-success">Terhubung</div>
                <small className="text-muted">{printer.deviceName}</small>
              </div>
            </>
          ) : (
            <>
              <FiX className="text-danger fs-3 me-3" />
              <div>
                <div className="fw-bold text-danger">Tidak Terhubung</div>
                <small className="text-muted">
                  Belum ada printer yang terhubung
                </small>
              </div>
            </>
          )}
        </div>

        <div className="d-grid gap-2">
          {!printer.connected ? (
            <button
              className="btn btn-primary"
              onClick={handleConnect}
              disabled={printerLoading}
            >
              <FiBluetooth className="me-2" />
              {printerLoading
                ? "Menghubungkan..."
                : "Hubungkan Printer Bluetooth"}
            </button>
          ) : (
            <>
              <button
                className="btn btn-success"
                onClick={handleTestPrint}
                disabled={printerLoading}
              >
                <FiPrinter className="me-2" />
                {printerLoading ? "Mencetak..." : "Test Print"}
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={handleDisconnect}
                disabled={printerLoading}
              >
                <FiX className="me-2" />
                Putuskan Koneksi
              </button>
            </>
          )}
        </div>

        <Alert variant="info" className="mt-3 mb-0 small">
          <strong>Panduan:</strong>
          <ListGroup variant="flush" className="mt-1">
            <ListGroup.Item className="bg-transparent border-0 px-0 py-1">
              + Gunakan browser <strong>Chrome</strong> atau{" "}
              <strong>Edge</strong>
            </ListGroup.Item>
            <ListGroup.Item className="bg-transparent border-0 px-0 py-1">
              + Pastikan printer sudah <strong>paired</strong> di Bluetooth
              sistem
            </ListGroup.Item>
            <ListGroup.Item className="bg-transparent border-0 px-0 py-1">
              + Printer harus mendukung <strong>ESC/POS</strong>
            </ListGroup.Item>
          </ListGroup>
        </Alert>
      </div>
    </div>
  );
};

export default Printer;
